import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  PreferencesState,
  PreferencesActions,
  KeyboardShortcut,
  DEFAULT_GENERAL_SETTINGS,
  DEFAULT_APPEARANCE_SETTINGS,
  DEFAULT_PROJECT_SETTINGS,
  DEFAULT_PERFORMANCE_SETTINGS,
  DEFAULT_ADVANCED_SETTINGS,
} from '../../core/types/preferences.types';

// Default keyboard shortcuts
const DEFAULT_SHORTCUTS: KeyboardShortcut[] = [
  { id: 'file.new', action: 'New Project', category: 'File', defaultKey: 'Ctrl+Shift+N', currentKey: 'Ctrl+Shift+N', isCustom: false },
  { id: 'file.open', action: 'Open Project', category: 'File', defaultKey: 'Ctrl+O', currentKey: 'Ctrl+O', isCustom: false },
  { id: 'file.save', action: 'Save', category: 'File', defaultKey: 'Ctrl+S', currentKey: 'Ctrl+S', isCustom: false },
  { id: 'file.saveAll', action: 'Save All', category: 'File', defaultKey: 'Ctrl+Shift+S', currentKey: 'Ctrl+Shift+S', isCustom: false },
  { id: 'edit.undo', action: 'Undo', category: 'Edit', defaultKey: 'Ctrl+Z', currentKey: 'Ctrl+Z', isCustom: false },
  { id: 'edit.redo', action: 'Redo', category: 'Edit', defaultKey: 'Ctrl+Shift+Z', currentKey: 'Ctrl+Shift+Z', isCustom: false },
  { id: 'edit.cut', action: 'Cut', category: 'Edit', defaultKey: 'Ctrl+X', currentKey: 'Ctrl+X', isCustom: false },
  { id: 'edit.copy', action: 'Copy', category: 'Edit', defaultKey: 'Ctrl+C', currentKey: 'Ctrl+C', isCustom: false },
  { id: 'edit.paste', action: 'Paste', category: 'Edit', defaultKey: 'Ctrl+V', currentKey: 'Ctrl+V', isCustom: false },
  { id: 'edit.delete', action: 'Delete', category: 'Edit', defaultKey: 'Delete', currentKey: 'Delete', isCustom: false },
  { id: 'edit.selectAll', action: 'Select All', category: 'Edit', defaultKey: 'Ctrl+A', currentKey: 'Ctrl+A', isCustom: false },
  { id: 'view.zoomIn', action: 'Zoom In', category: 'View', defaultKey: 'Ctrl+=', currentKey: 'Ctrl+=', isCustom: false },
  { id: 'view.zoomOut', action: 'Zoom Out', category: 'View', defaultKey: 'Ctrl+-', currentKey: 'Ctrl+-', isCustom: false },
  { id: 'view.resetZoom', action: 'Reset Zoom', category: 'View', defaultKey: 'Ctrl+0', currentKey: 'Ctrl+0', isCustom: false },
  { id: 'scene.play', action: 'Play', category: 'Scene', defaultKey: 'Ctrl+P', currentKey: 'Ctrl+P', isCustom: false },
  { id: 'scene.pause', action: 'Pause', category: 'Scene', defaultKey: 'Ctrl+Shift+P', currentKey: 'Ctrl+Shift+P', isCustom: false },
  { id: 'scene.stop', action: 'Stop', category: 'Scene', defaultKey: 'Ctrl+.', currentKey: 'Ctrl+.', isCustom: false },
  { id: 'tools.move', action: 'Move Tool', category: 'Tools', defaultKey: 'W', currentKey: 'W', isCustom: false },
  { id: 'tools.rotate', action: 'Rotate Tool', category: 'Tools', defaultKey: 'E', currentKey: 'E', isCustom: false },
  { id: 'tools.scale', action: 'Scale Tool', category: 'Tools', defaultKey: 'R', currentKey: 'R', isCustom: false },
  { id: 'tools.pan', action: 'Pan Tool', category: 'Tools', defaultKey: 'Q', currentKey: 'Q', isCustom: false },
];

interface PreferencesStore extends PreferencesState, PreferencesActions {
  backendApiUrl: string;
  setBackendApiUrl: (url: string) => void;
}

export const usePreferencesStore = create<PreferencesStore>()(
  persist(
    immer((set, get) => ({
      // Backend API URL
      backendApiUrl: 'https://pixi.cyberyokai.com/api/v1/games',
      setBackendApiUrl: (url: string) => {
        set((state) => {
          state.backendApiUrl = url;
        });
      },

      // Initial state
      preferences: {
        general: DEFAULT_GENERAL_SETTINGS,
        appearance: DEFAULT_APPEARANCE_SETTINGS,
        keyboard: { shortcuts: DEFAULT_SHORTCUTS },
        projects: DEFAULT_PROJECT_SETTINGS,
        performance: DEFAULT_PERFORMANCE_SETTINGS,
        advanced: DEFAULT_ADVANCED_SETTINGS,
      },
      extensions: [],

      // Settings updates
      updateGeneral: (settings) => {
        set((state) => {
          state.preferences.general = { ...state.preferences.general, ...settings };
        });
      },

      updateAppearance: (settings) => {
        set((state) => {
          state.preferences.appearance = { ...state.preferences.appearance, ...settings };
        });
      },

      updateKeyboard: (shortcuts) => {
        set((state) => {
          state.preferences.keyboard.shortcuts = shortcuts;
        });
      },

      updateProjects: (settings) => {
        set((state) => {
          state.preferences.projects = { ...state.preferences.projects, ...settings };
        });
      },

      updatePerformance: (settings) => {
        set((state) => {
          state.preferences.performance = { ...state.preferences.performance, ...settings };
        });
      },

      updateAdvanced: (settings) => {
        set((state) => {
          state.preferences.advanced = { ...state.preferences.advanced, ...settings };
        });
      },

      // Shortcuts
      setShortcut: (id, key) => {
        set((state) => {
          const shortcut = state.preferences.keyboard.shortcuts.find(s => s.id === id);
          if (shortcut) {
            shortcut.currentKey = key;
            shortcut.isCustom = key !== shortcut.defaultKey;
          }
        });
      },

      resetShortcut: (id) => {
        set((state) => {
          const shortcut = state.preferences.keyboard.shortcuts.find(s => s.id === id);
          if (shortcut) {
            shortcut.currentKey = shortcut.defaultKey;
            shortcut.isCustom = false;
          }
        });
      },

      resetAllShortcuts: () => {
        set((state) => {
          for (const shortcut of state.preferences.keyboard.shortcuts) {
            shortcut.currentKey = shortcut.defaultKey;
            shortcut.isCustom = false;
          }
        });
      },

      // Extensions
      toggleExtension: (id) => {
        set((state) => {
          const ext = state.extensions.find(e => e.id === id);
          if (ext) {
            ext.enabled = !ext.enabled;
          }
        });
      },

      // Reset
      resetToDefaults: () => {
        set((state) => {
          state.preferences = {
            general: DEFAULT_GENERAL_SETTINGS,
            appearance: DEFAULT_APPEARANCE_SETTINGS,
            keyboard: { shortcuts: DEFAULT_SHORTCUTS },
            projects: DEFAULT_PROJECT_SETTINGS,
            performance: DEFAULT_PERFORMANCE_SETTINGS,
            advanced: DEFAULT_ADVANCED_SETTINGS,
          };
        });
      },

      // Import/Export
      exportSettings: () => {
        const { preferences } = get();
        return JSON.stringify(preferences, null, 2);
      },

      importSettings: (data) => {
        try {
          const imported = JSON.parse(data);
          set((state) => {
            state.preferences = imported;
          });
        } catch {
          console.error('Failed to import settings');
        }
      },
    })),
    {
      name: 'preferences-store',
      partialize: (state) => ({
        preferences: state.preferences,
        extensions: state.extensions,
        backendApiUrl: state.backendApiUrl,
      }),
    }
  )
);
